import { FC, useEffect, useState } from "react";
import dayjs from 'dayjs';
import { fetchAsync, postAsync } from "../../services/ApiHandler";
import { getToken, isLoggedIn } from "../../services/CookieHandler";
import { GenerationRequest } from "../../model/GenerationRequest";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { ComboboxItem, Loader, Select } from "@mantine/core";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FooterMenu } from "../../components/FooterMenu";
import DownloadIcon from "../../assets/icon/download.png";
import TrashIcon from "../../assets/icon/icons8-trash-96.png";

type ConfirmActionState = {
  type: 'delete' | 'download';
  ids: string[];
  origin: 'selection' | 'detail';
};

export const GenerationHistoryPage: FC = () => {
  const navigate = useNavigate();
  const MAX_SELECTION = 10;
  const [requestList, setRequestList] = useState<GenerationRequest[] | undefined>(undefined);
  const [selectedRequest, setSelectedRequest] = useState<GenerationRequest | undefined>(undefined);
  const [hasNextData, setHasNextData] = useState(true);
  const [orderValue, setOrderValue] = useState<ComboboxItem>({ value: 'desc', label: '新しい順' });
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [version, setVersion] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pendingAction, setPendingAction] = useState<ConfirmActionState | null>(null);

  useEffect(() => {
    if (version > 0) {
      if (!isLoggedIn()) return;
      fetchGeneratedList();
    }
  }, [version]);

  async function fetchGeneratedList(){
    var param = { count: 50, page: page, asc: orderValue!.value === 'asc', containsNsfw: false, containsError: false };
    const list = await fetchAsync<GenerationRequest[]>('Generation/GetGeneratedImages', param);
  
    // 関数型アップデートを使用してステート更新
    setRequestList((prevRequestList) => {
      if (!prevRequestList || page === 0) {
        return list;
      }
      return prevRequestList.concat(list); // 直前のリストと新しいリストを結合
    });

    if(list.length === 0) {
      setHasNextData(false); // 次のデータがない場合の処理
    }
  };

  function toggleSelectionMode() {
    setIsSelectionMode((prev) => {
      if (prev) {
        setSelectedIds([]);
      }
      return !prev;
    });
  }

  function toggleImageSelection(requestId: string) {
    setSelectedIds((prev) => {
      if (prev.includes(requestId)) {
        return prev.filter((id) => id !== requestId);
      }
      return [...prev, requestId];
    });
  }

  function openConfirm(action: 'delete' | 'download', targetIds?: string[], origin: 'selection' | 'detail' = 'selection') {
    const ids = targetIds ?? selectedIds;
    if (ids.length === 0) return;
    if (action === 'download' && ids.length > MAX_SELECTION) {
      alert(`ダウンロードは一度に最大${MAX_SELECTION}件までです。`);
      return;
    }
    setPendingAction({ type: action, ids, origin });
  }

  function closeConfirm() {
    setPendingAction(null);
  }

  async function handleDelete(ids: string[], origin: 'selection' | 'detail') {
    if (ids.length === 0 || isActionLoading) return;
    setIsActionLoading(true);
    try {
      const response = await postAsync<string>('Generation/DeleteImages', { ids });
      if ((response as any)?.code === 1) {
        setRequestList((prev) => prev ? prev.filter((item) => !ids.includes(item.requestId)) : prev);
        if (origin === 'selection') {
          setSelectedIds([]);
          setIsSelectionMode(false);
        } else {
          setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        }
        setSelectedRequest((prev) => (prev && ids.includes(prev.requestId) ? undefined : prev));
      } else if ((response as any)?.message) {
        alert((response as any).message);
      }
    } catch (error) {
      console.error(error);
      alert('削除に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsActionLoading(false);
    }
  }

  async function handleDownload(ids: string[]) {
    if (ids.length === 0 || isDownloading) return;
    setIsDownloading(true);
    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ZipDownload/Download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ids })
      });
      if (!response.ok) {
        throw new Error('ダウンロードに失敗しました');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-images-${dayjs().format('YYYYMMDD-HHmmss')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('ダウンロードに失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsDownloading(false);
    }
  }

  function handleConfirmOk() {
    if (!pendingAction) return;
    const action = pendingAction;
    closeConfirm();
    if (action.type === 'delete') {
      handleDelete(action.ids, action.origin);
    } else if (action.type === 'download') {
      handleDownload(action.ids);
    }
  }

  function handleUsePrompt(request: GenerationRequest) {
    const prompt = `${request.prompt ?? ''} ${request.additionalPrompt ?? ''}`.trim();
    const negativePrompt = `${request.negativePrompt ?? ''} ${request.additionalNegativePrompt ?? ''}`.trim();
    setSelectedRequest(undefined);
    navigate('/image-generation', {
      state: {
        prefillPrompt: prompt,
        prefillNegativePrompt: negativePrompt,
        prefillTasteName: request.tasteName,
      }
    });
  }

  async function getNextData(){
    if(isProcessing) {
      return;
    }

    if(hasNextData === false){
      return;
    }

    setPage(prevPage => prevPage + 1);
    setVersion(prevVersion => prevVersion + 1);
  }

  useEffect(() => {
    // fetchGeneratedList();
    listenScrollEvent();
  }, [])

  useEffect(() => {
    setRequestList([]);
    setPage(0);
    setVersion(prevVersion => prevVersion + 1);
    setHasNextData(true);

  }, [orderValue])

  function listenScrollEvent() {
    window.addEventListener('scroll', function() {
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const documentHeight = document.body.scrollHeight;
      if (windowHeight + scrollTop >= documentHeight - 20) {
        getNextData();
      }
    });
  }

  function buildDialog(){
    return <>
    <div id="processing-dialog" className="dialog-overlay">
      <div className='text-start max-h-[80vh] overflow-scroll w-[95%] sm:w-[90%] lg:w-[1200px] bg-white p-5 rounded-lg shadow-lg' style={{ maxWidth: "1400px", paddingTop: '0px' }}>
        <div className="pb-10 pt-4 bg-white" style={{ position: 'sticky', top: '0px'}}>
          <div className="mb-2 text-lg flex justify-end absolute w-full">
            <img onClick={(e) => setSelectedRequest(undefined)} src="/images/icon-close.svg" className="clickable w-[32px]"></img>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-[50%] flex items-center sm:items-start min-h-[200px] sm:min-h-[500px] justify-center sm:justify-start">
            <img className="w-full max-w-[600px] px-4 sm:px-0"  src={`${process.env.REACT_APP_IMAGE_URL}/${selectedRequest!.requestId}`} />
          </div>
          <div className="w-full sm:w-[50%] px-4 sm:ml-4 sm:px-0 pb-4">
            <div className="text-sm">生成日時</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.createdDate ? dayjs(selectedRequest!.createdDate).format('YYYY-M-D H:mm') : '' }
            </div>
            <div className="text-sm">モデル</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.tasteName }
            </div>
            <div className="text-sm mt-2">シード値</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.seed }
            </div>
            <div className="text-sm mt-2">ステップ数</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.inferenceSteps }
            </div>
            <div className="text-sm mt-2">スケール</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.guidanceScale }
            </div>
            <div className="text-sm mt-2">アスペクト比</div>
            <div className="text-sm border py-1 px-2 rounded">
              { `${selectedRequest!.aspectRatio} (${selectedRequest!.width}px x ${selectedRequest!.height}px)` }
            </div>
            <div className="text-sm mr-4 mt-4">プロンプト</div>
            <div className="text-sm border py-1 px-2 min-h-7 rounded">
              { `${selectedRequest!.prompt} ${selectedRequest!.additionalPrompt}` }
            </div>
            <>
            <div className="text-sm mt-4">ネガティブプロンプト</div>
            <div className="text-sm border py-1 px-2 min-h-7 rounded">
              { `${selectedRequest!.negativePrompt} ${selectedRequest!.additionalNegativePrompt}` }
            </div>
            </>
            <div className="mt-6 space-y-3">
              <button
                className="w-full gradation-button py-2 px-4 rounded font-bold clickable"
                onClick={() => handleUsePrompt(selectedRequest!)}
              >
                このプロンプトを利用する
              </button>
              <button
                className="w-full text-sm font-semibold text-red-600 py-2"
                onClick={() => openConfirm('delete', [selectedRequest!.requestId], 'detail')}
              >
                この画像を削除する
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
  }

  return <>
    <Helmet>
      <title>生成履歴 - Drop AI</title>
      <meta name="description" content="生成履歴でお客さまの画像生成履歴を確認いただけます。" />
      <meta property="og:url" content="https://drop4i.com/generation-history" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="生成履歴 - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="生成履歴でお客さまの画像生成履歴を確認いただけます。" />
      <style>{`
        .history-grid {
          display: grid !important;
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 8px;
        }
        @media (min-width: 640px) {
          .history-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        @media (min-width: 768px) {
          .history-grid {
            grid-template-columns: repeat(5, 1fr) !important;
          }
        }
      `}</style>
    </Helmet>
    { selectedRequest &&
      buildDialog()
    }
    <div className="max-h-[calc(100vh-76px)] sm:max-h-[calc(100vh-76px)] overflow-scroll relative pb-16 sm:pb-0">
      <div className="flex items-center mb-1 mt-4 justify-between sm:justify-start">
        <div className="text-[24px] sm:text-[30px] ml-3 sm:ml-0">生成履歴</div>
        <div className="flex items-center ml-2 sm:ml-6">
          <button
            onClick={toggleSelectionMode}
            className="px-4 py-2 text-sm rounded border bg-white text-gray-800"
          >
            {isSelectionMode ? 'キャンセル' : '選択'}
          </button>
          <Select
            className="ml-2 mr-10 sm:mr-0"
            size="sm"
            styles={{ input: { minWidth: '110px', width: '110px', paddingLeft: '8px', paddingRight: '24px' } }}
            data={[
              { value: 'asc', label: '古い順' },
              { value: 'desc', label: '新しい順' }
            ]}
            value={orderValue ? orderValue.value : null}
            onChange={(_value, option) => setOrderValue(option)}
          />
          {/* Mobile close button */}
          <button 
            onClick={() => navigate(-1)} 
            className="sm:hidden absolute top-4 right-0.5 p-2 clickable"
          >
            <img src="/images/icon-close.svg" className="w-[24px] h-[24px]" alt="閉じる" />
          </button>
        </div>
      </div>
      {isSelectionMode &&
        <div className="text-xs text-gray-500 ml-3 sm:ml-0 mb-1">
          ダウンロードは一度に最大{MAX_SELECTION}件まで選択できます。
        </div>
      }
      { requestList === undefined &&
        <div className="min-h-[calc(100vh-76px)] h-[900px]"> 
        </div>
      }
      { requestList !== undefined &&
      <div className="history-grid min-h-[calc(100vh-76px)] mt-2 m-auto max-w-full sm:max-w-[1200px] w-full sm:w-[1200px] bg-white p-1 sm:p-0"> 
        { requestList?.map((e) => (
          <div
            key={e.requestId}
            className={`relative ${isSelectionMode && selectedIds.includes(e.requestId) ? 'ring-4 ring-blue-500 rounded-lg' : ''}`}
            onClick={() => {
              if (isSelectionMode) {
                toggleImageSelection(e.requestId);
              } else {
                setSelectedRequest(e);
              }
            }}
          >
            {isSelectionMode &&
              <div className="absolute top-2 right-2 z-10 pointer-events-none bg-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold text-gray-800 border border-gray-300 shadow">
                {selectedIds.includes(e.requestId) ? '✓' : ''}
              </div>
            }
            <img
              className="w-full sm:max-w-[240px] rounded-lg cursor-pointer transition-opacity duration-150 hover:opacity-70"
              src={`${process.env.REACT_APP_IMAGE_URL}/${e.requestId}_mid`}
            />
          </div>
        ))}
        </div>
      }
    </div>
    {isSelectionMode &&
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 flex justify-center gap-6 z-20">
        <button
          aria-label="削除"
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center"
          disabled={selectedIds.length === 0 || isActionLoading}
          onClick={() => openConfirm('delete')}
        >
          <img src={TrashIcon} alt="削除" className="w-5 h-5" />
        </button>
        <button
          aria-label="ダウンロード"
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center relative"
          disabled={selectedIds.length === 0 || isDownloading}
          onClick={() => openConfirm('download')}
        >
          <img src={DownloadIcon} alt="ダウンロード" className={`w-5 h-5 ${isDownloading ? 'opacity-0' : 'opacity-100'}`} />
          {isDownloading &&
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader size="xs" color="dark" />
            </div>
          }
        </button>
      </div>
    }
    {pendingAction &&
      <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center px-4 py-8">
        <div className="w-full sm:max-w-[360px]">
          <div className="bg-[#f2f2f7] rounded-2xl text-center shadow-lg overflow-hidden">
            <div className="bg-white px-4 py-6 text-sm text-gray-800 whitespace-pre-line">
              {pendingAction.type === 'download'
                ? `選択した${pendingAction.ids.length}件の画像をダウンロードしますか？`
                : pendingAction.origin === 'detail'
                  ? '削除した生成履歴は復元できません。\n削除しますか？'
                  : `選択した${pendingAction.ids.length}件の画像を削除しますか？\nなお、削除した画像は復元できません。`}
            </div>
            <div className="border-t border-gray-200 flex">
              <button
                className="flex-1 py-3 text-blue-500 text-base font-semibold"
                onClick={closeConfirm}
              >
                キャンセル
              </button>
            </div>
            <div className="border-t border-gray-200 flex">
              <button
                className="flex-1 py-3 text-blue-500 text-base font-semibold disabled:opacity-40"
                onClick={handleConfirmOk}
                disabled={(pendingAction.type === 'delete' && isActionLoading) || (pendingAction.type === 'download' && isDownloading)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    }
    <FooterMenu />
  </>;
}
  
